<template>
  <section class="px-5 md:px-3 mt-28">
    <div class="grid grid-cols-12 items-start gap-3">
      <div class="col-span-12 grid gap-y-3 mb-12">
        <h4 v-if="!$route.params.organizationId">
          {{ $t("labels.add", { pt: "Organização", en: "Organization" }) }}
        </h4>

        <h4 v-else>
          {{ $t("labels.edit", { pt: "Organização", en: "Organization" }) }}
        </h4>
        <form
          @submit.prevent="save()"
          class="grid grid-cols-12 gap-x-5"
          autocomplete="off"
        >
          <div class="flex flex-col my-4 col-span-12 md:col-span-6">
            <p class="mb-2 text-black">
              {{
                $t("labels.generic", {
                  pt: "Nome da empresa",
                  en: "Company name",
                })
              }}
            </p>
            <InputText
              id="org-name-input"
              type="text"
              v-model="form.name"
              :class="v$.form.name.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.name.$errors" />
          </div>
          <div class="flex flex-col my-4 col-span-12 md:col-span-6">
            <p class="mb-2 text-black">CNPJ</p>
            <InputMask
              mask="99.999.999/9999-99"
              id="org-cnpj-input"
              type="text"
              v-model="form.cnpj"
              :class="v$.form.cnpj.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.cnpj.$errors" />
          </div>
          <div class="flex flex-col my-4 col-span-8 lg:col-span-5">
            <p class="mb-2 text-black">CEP</p>
            <InputMask
              mask="99.999-999"
              id="org-cep-input"
              type="text"
              v-model="form.address.cep"
              @blur="getAddressByCEP"
              :class="v$.form.address.cep.$error || cepError ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.cep.$errors" />
          </div>
          <div class="flex flex-col my-4 col-span-4 lg:col-span-2">
            <p class="mb-2 text-black">
              {{ $t("labels.generic", { pt: "Estado", en: "State" }) }}
            </p>
            <InputText
              id="org-uf-input"
              type="text"
              v-model="form.address.state"
              :class="v$.form.address.state.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.state.$errors" />
          </div>
          <div class="flex flex-col my-4 col-span-12 lg:col-span-5">
            <p class="mb-2 text-black">
              {{ $t("labels.generic", { pt: "Cidade", en: "City" }) }}
            </p>
            <InputText
              id="org-city-input"
              type="text"
              v-model="form.address.city"
              :class="v$.form.address.city.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.city.$errors" />
          </div>
          <div
            class="flex flex-col my-4 col-span-9 md:col-span-6 lg:col-span-5"
          >
            <p class="mb-2 text-black">
              {{ $t("labels.generic", { pt: "Logradouro", en: "Street" }) }}
            </p>
            <InputText
              id="org-street-input"
              type="text"
              v-model="form.address.street"
              :class="v$.form.address.street.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.street.$errors" />
          </div>

          <div
            class="flex flex-col my-4 col-span-3 md:col-span-6 lg:col-span-1"
          >
            <p class="mb-2 text-black">
              {{ $t("labels.generic", { pt: "Número", en: "Number" }) }}
            </p>
            <InputText
              id="org-number-input"
              type="text"
              v-model="form.address.number"
              :class="v$.form.address.number.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.number.$errors" />
          </div>
          <div
            class="flex flex-col my-4 col-span-12 md:col-span-6 lg:col-span-3"
          >
            <p class="mb-2 text-black">
              {{ $t("labels.generic", { pt: "Bairro", en: "District" }) }}
            </p>
            <InputText
              id="org-district-input"
              type="text"
              v-model="form.address.district"
              :class="v$.form.address.district.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="v$.form.address.district.$errors" />
          </div>
          <div
            class="flex flex-col my-4 col-span-12 md:col-span-6 lg:col-span-3"
          >
            <p class="mb-2 text-black">
              {{
                $t("labels.generic", { pt: "Complemento", en: "Complement" })
              }}
            </p>
            <InputText
              id="org-complement-input"
              v-model="form.address.complement"
              type="text"
            />
          </div>
          <div
            class="col-span-12 bg-gray-200 p-4 mt-8 flex justify-between items-center rounded-md shadow-md"
          >
            <p class="text-black">
              {{
                $t("labels.generic", {
                  pt: "Contatos cadastrados: ",
                  en: "Saved contacts: ",
                })
              }}{{ countContacts }}
            </p>
            <Button
              id="btn-add-contact"
              class="btn-simple flex"
              @click="addContact()"
            >
              <v-icon name="md-add"></v-icon>
              {{
                $t("labels.generic", {
                  pt: "Adicionar contato",
                  en: "Add contact",
                })
              }}
            </Button>
          </div>
          <div
            class="col-span-12 grid grid-cols-12 gap-x-5 mt-8 gap-y-3 items-end"
            v-for="(contact, i) in form.contacts"
            :key="i"
          >
            <div class="col-span-12 mb-4">
              <h6 class="text-black">
                {{ $t("labels.generic", { pt: "Contato", en: "Contact" }) }}
                {{ i == 0 ? "principal" : i + 1 }}
              </h6>
            </div>
            <div class="col-span-12 lg:col-span-3">
              <p class="mb-2 text-black">
                {{ $t("labels.generic", { pt: "Setor", en: "Department" }) }}
              </p>
              <InputText
                :id="`org-contact-department-input-${i + 1}`"
                v-model="contact.department"
                type="text"
                :class="
                  v$.form.contacts.$each.$response.$errors[i].department
                    ?.length !== 0 && v$.form.$error
                    ? 'p-invalid'
                    : ''
                "
              />
              <Errors
                v-if="v$.form.contacts.$dirty"
                :errors="v$.form.contacts.$each.$response.$errors[i].department"
              >
                <template #msg>
                  {{ $t("validations.required") }}
                </template></Errors
              >
            </div>
            <div class="col-span-12 lg:col-span-3">
              <p class="mb-2 text-black">
                {{ $t("labels.generic", { pt: "Nome", en: "Name" }) }}
              </p>
              <InputText
                :id="`org-contact-name-input-${i + 1}`"
                v-model="contact.name"
                type="text"
                :class="
                  v$.form.contacts.$each.$response.$errors[i].name?.length !==
                    0 && v$.form.$error
                    ? 'p-invalid'
                    : ''
                "
              />
              <Errors
                v-if="v$.form.contacts.$dirty"
                :errors="v$.form.contacts.$each.$response.$errors[i].name"
              >
                <template #msg>
                  {{ $t("validations.required") }}
                </template></Errors
              >
            </div>
            <div class="col-span-12 lg:col-span-3">
              <p class="mb-2 text-black">E-mail</p>
              <InputText
                :id="`org-contact-email-input-${i + 1}`"
                v-model="contact.email"
                type="text"
                :class="
                  v$.form.contacts.$each.$response.$errors[i].email?.length !==
                    0 && v$.form.$error
                    ? 'p-invalid'
                    : ''
                "
              />
              <Errors
                v-if="v$.form.contacts.$dirty"
                :errors="v$.form.contacts.$each.$response.$errors[i].email"
              >
                <template #msg>
                  {{ $t("validations.required") }}
                </template></Errors
              >
            </div>

            <div
              :class="` ${
                i + 1 === form.contacts.length && i > 0
                  ? 'col-span-6 lg:col-span-2'
                  : 'col-span-12 lg:col-span-3'
              }`"
            >
              <p class="mb-2 text-black">
                {{ $t("labels.generic", { pt: "Telefone", en: "Telephone" }) }}
              </p>
              <InputMask
                mask="(99) 999999999"
                :id="`org-contact-telephone-input-${i + 1}`"
                v-model="contact.phone"
                type="text"
                :class="
                  v$.form.contacts.$each.$response.$errors[i].phone?.length !==
                    0 && v$.form.$error
                    ? 'p-invalid'
                    : ''
                "
              />
              <Errors
                v-if="v$.form.contacts.$dirty"
                :errors="v$.form.contacts.$each.$response.$errors[i].phone"
              >
                <template #msg>
                  {{ $t("validations.required") }}
                </template></Errors
              >
            </div>
            <div
              class="col-span-6 lg:col-span-1 flex flex-col"
              v-if="i + 1 === form.contacts.length && i > 0"
            >
              <button
                class="w-full p-0 h-[46px] flex justify-start items-center"
                :id="`btn-remove-contact-${i + 1}`"
                title="Remover contato"
                @click="form.contacts.pop()"
              >
                <v-icon class="text-h-error-400" name="md-delete"></v-icon>
              </button>
              <p
                class="mt-5"
                v-if="
                  v$.form.contacts.$each.$response.$errors[i].phone?.length !==
                    0 && v$.form.$error
                "
              ></p>
            </div>
          </div>
          <div class="col-span-12 flex mt-8 gap-4">
            <Button
              @click="confirmModal()"
              id="btn-cancel-organization"
              class="btn-outline h-10"
              >{{
                $t("labels.generic", { pt: "Cancelar", en: "Cancel" })
              }}</Button
            >
            <Button
              :loading="btnLoading"
              type="submit"
              id="btn-save-organization"
              class="btn-primary h-10"
              >{{ $t("labels.generic", { pt: "Salvar", en: "Save" }) }}</Button
            >
          </div>
        </form>
      </div>
    </div>
    <ConfirmDialog />
  </section>
</template>
<script lang="ts">
import { or, required } from "@/locale/locale.config";
import { ViaCEP } from "@/models/address.model";
import { Contact, Organization } from "@/models/organization.model";
import { OrganizationsRest } from "@/services/rest/organizations.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { validCNPJ } from "@/utils/regex.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import useVuelidate from "@vuelidate/core";
import { helpers, required as req } from "@vuelidate/validators";
import { last, take } from "rxjs";
import { defineComponent } from "vue";
import { OrganizationsService } from "../organizations.service";

export default defineComponent({
  name: "forms-organizations",
  data() {
    return {
      form: new Organization(),
      address: new ViaCEP(),
      cepError: false as boolean,
      btnLoading: false as boolean,
      countContacts: 0 as number,
    };
  },
  watch: {
    "form.contacts": {
      handler: function (val, oldVal) {
        this.countContacts = this.getCompleteContacts();
      },
      deep: true,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        name: {
          required,
        },
        cnpj: {
          validCNPJ: or((value: any) => {
            return validCNPJ(value);
          }),
        },
        address: {
          cep: {
            required,
          },
          state: {
            required,
          },
          city: {
            required,
          },
          street: {
            required,
          },
          number: {
            required,
          },
          district: {
            required,
          },
        },
        contacts: {
          $each: helpers.forEach({
            name: { req },
            department: { req },
            email: { req },
            phone: { req },
          }),
        },
      },
    };
  },
  mounted() {
    if (this.$route.params.organizationId) {
      this.getOrganizationById(String(this.$route.params.organizationId));
    }
  },
  computed: {
    service(): OrganizationsService {
      return new OrganizationsService();
    },
    rest(): OrganizationsRest {
      return new OrganizationsRest();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  methods: {
    getOrganizationById(organizationId: string): void {
      this.service.organization.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.form = response;
        },
      });
      this.service.getOrganizationById(organizationId);
    },
    save(): void {
      this.v$.form.$touch();
      if (!this.v$.form.$error) {
        this.btnLoading = true;
        if (this.form.id) {
          this.editOrganization();
        } else {
          this.createOrganization();
        }
      }
    },
    createOrganization(): void {
      this.rest.postOrganization(this.form).subscribe({
        next: (response) => {
          this.$toast.add(ToastFactory.success(Messages.ORGANIZATION_ADDED));
          this.go.redirectToPage("theme", {
            id: response.id,
          });
          this.btnLoading = false;
        },
      });
    },
    editOrganization(): void {
      this.rest.putOrganization(String(this.form.id), this.form).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.ORGANIZATION_EDIT));
          this.go.redirectToPage("organizations");
          this.btnLoading = false;
        },
      });
    },
    getAddressByCEP(): void {
      this.service.address.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.address = response;
          this.form.address = {
            cep: this.address.cep,
            city: this.address.localidade,
            state: this.address.uf,
            street: this.address.logradouro,
            district: this.address.bairro,
            number: this.form.address.number,
            country: "Brasil",
          };
        },
      });

      if (this.form.address.cep) {
        let cep = this.form.address.cep.replace(/\D/g, "");
        this.service.getAddressByCEP(cep);
      }
    },
    addContact(): void {
      this.form.contacts.push(new Contact());
    },
    getCompleteContacts(): number {
      let countCompleteContacts = 0;
      this.form.contacts.forEach((element) => {
        if (
          element.name &&
          element.department &&
          element.email &&
          element.phone
        ) {
          countCompleteContacts++;
        }
      });
      return countCompleteContacts;
    },
    confirmModal(): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: "Deseja sair da tela de cadastro de organização?",
          en: "Do you want to exit the organization registration screen?",
        }),
        header: `Sair da tela`,
        acceptLabel: "Sair",
        rejectLabel: "Não",
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.go.redirectTo("organizations");
        },
      });
    },
  },
});
</script>
